import React, { useEffect, useMemo, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { X, Loader2, CreditCard, ShieldCheck, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";

export function usePaymentPolling(sessionId, onPaid) {
  const [state, setState] = useState("idle");
  useEffect(() => {
    if (!sessionId) return;
    let tries = 0, stop = false;
    setState("checking");
    const tick = async () => {
      if (stop) return;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") { setState("paid"); onPaid?.(r.data); return; }
        if (["expired", "failed"].includes(r.data.payment_status)) { setState("failed"); return; }
      } catch { /* retry */ }
      if (++tries < 20) setTimeout(tick, 2000); else setState("timeout");
    };
    tick();
    return () => { stop = true; };
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps
  return state;
}

export default function CheckoutModal({ tournament, price = 5, onClose, onPaid }) {
  const [session, setSession] = useState(null);
  const [err, setErr] = useState("");
  const [doneId, setDoneId] = useState(null);
  const poll = usePaymentPolling(doneId, onPaid);

  useEffect(() => {
    api.post("/payments/tournament-checkout", { tournament_id: tournament.id, origin_url: window.location.origin })
      .then((r) => setSession(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Could not start the payment"));
  }, [tournament.id]);

  const stripePromise = useMemo(() => (session?.mode === "embedded" ? loadStripe(session.publishable_key) : null), [session]);

  return (
    <div className="fixed inset-0 z-[120] grid place-items-center p-4" data-testid="checkout-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={onClose} />
      <div className="relative w-full max-w-lg max-h-[92vh] overflow-y-auto card-surface rounded-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-white/10 bg-[hsl(var(--card))]">
          <div>
            <div className="eyebrow text-white/40">Complete payment</div>
            <div className="font-bold text-white truncate max-w-[300px]">{tournament.name}</div>
          </div>
          <button onClick={onClose} className="p-1.5 text-white/50 hover:text-white" data-testid="checkout-close"><X size={18} /></button>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-between rounded-xl bg-black/30 px-4 py-3 mb-5">
            <span className="text-sm text-white/60">Tournament creation fee</span>
            <span className="text-xl font-bold font-head text-white" data-testid="checkout-price">${price.toFixed(2)}</span>
          </div>
          {err && <div className="text-sm text-red-400 bg-red-500/10 rounded-lg px-3 py-2" data-testid="checkout-error">{err}</div>}
          {!session && !err && <div className="py-10 grid place-items-center text-white/50"><Loader2 className="animate-spin" /></div>}
          {poll === "paid" ? (
            <div className="py-8 text-center" data-testid="checkout-success"><CheckCircle2 size={40} className="mx-auto text-green-400" /><div className="mt-3 font-bold text-white">Payment successful</div><div className="text-sm text-white/50">Your tournament is now live.</div></div>
          ) : doneId ? (
            <div className="py-8 text-center text-white/60"><Loader2 className="animate-spin mx-auto mb-2" />Confirming payment…</div>
          ) : session?.mode === "embedded" ? (
            <div className="rounded-xl overflow-hidden bg-white" data-testid="stripe-embedded-checkout">
              <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret: session.client_secret, onComplete: () => setDoneId(session.session_id) }}>
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </div>
          ) : session?.mode === "hosted" ? (
            <a href={session.url} data-testid="checkout-continue-btn" className="w-full py-3 rounded-full bg-[hsl(var(--primary))] text-white font-bold flex items-center justify-center gap-2 hover:brightness-110"><CreditCard size={16} /> Continue to secure payment</a>
          ) : null}
          <p className="mt-4 flex items-center justify-center gap-1.5 text-[11px] text-white/40"><ShieldCheck size={13} /> Payments are processed securely by Stripe.</p>
        </div>
      </div>
    </div>
  );
}
